import React from "react";
import { estadoInteresLabel } from "../adapters/interesadosAdapter";

const estadoBadgeClass = (estado) => {
  switch ((estado || "").toLowerCase()) {
    case "interesado":
      return "bg-primary-subtle text-primary";
    case "prematriculado":
      return "bg-warning-subtle text-warning";
    case "matriculado":
      return "bg-success-subtle text-success";
    case "descartado":
      return "bg-danger-subtle text-danger";
    default:
      return "bg-info-subtle text-info";
  }
};

export default function InteresadosTable({
  interesados,
  loading,
  error,
  reload,
  seleccionadoId,
  onSelect,
  onEdit,
  onDelete,
  onMatricular,
  matriculandoId = null,
  canEdit = true,
  canDelete = true,
  maxHeight = "60vh",
}) {
  if (loading) {
    return (
      <div className="management-card text-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="management-card management-alert d-flex justify-content-between align-items-center">
        <span>{error}</span>
        {reload && (
          <button className="btn btn-outline-secondary btn-sm" onClick={reload}>
            Reintentar
          </button>
        )}
      </div>
    );
  }

  if (!interesados.length) {
    return (
      <div className="management-card dashboard-empty text-center py-5">
        <i className="bi bi-people dashboard-empty-icon mb-3" />
        <h5 className="fw-semibold mb-1">No hay interesados registrados.</h5>
        <p className="text-muted mb-0">Crea un interesado o ajusta los filtros.</p>
      </div>
    );
  }

  return (
    <div className="management-card int-table-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <p className="management-label mb-1">Listado</p>
          <h6 className="mb-0">Interesados registrados</h6>
        </div>
        {reload && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={reload}
            disabled={loading}
          >
            <i className="bi bi-arrow-repeat me-2" />
            Actualizar
          </button>
        )}
      </div>
      <div className="management-table-wrapper" style={{ maxHeight }}>
        <table className="table align-middle management-table int-table mb-0">
          <thead className="bg-transparent">
            <tr>
              <th>#</th>
              <th>Interesado</th>
              <th>Documento</th>
              <th>Contacto</th>
              <th>Fuente</th>
              <th>Empresa</th>
              <th className="text-center">Estado</th>
              <th className="text-end">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {interesados.map((item, idx) => {
              const rowKey = item.id !== undefined && item.id !== null ? `int-${item.id}-${idx}` : `int-temp-${idx}`;
              const isSelected = seleccionadoId === item.id;
              return (
                <tr
                  key={rowKey}
                  className={isSelected ? "selected-row" : ""}
                  onClick={() => onSelect?.(item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect?.(item);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <td className="fw-semibold">{idx + 1}</td>
                  <td>
                    <div className="fw-semibold">{item.nombreCompleto || "Sin nombre"}</div>
                    <small className="text-muted">
                      {item.creadoEn ? new Date(item.creadoEn).toLocaleDateString() : "Sin fecha"}
                    </small>
                    {item.referidoNombre && (
                      <div className="small text-primary">
                        Referido por: {item.referidoNombre}
                        {item.referidoDocumento ? ` (${item.referidoDocumento})` : ""}
                      </div>
                    )}
                  </td>
                  <td>{item.documento || "N/D"}</td>
                  <td>
                    <div>{item.telefono || "Sin telefono"}</div>
                    <small className="text-muted">{item.email || "Sin correo"}</small>
                  </td>
                  <td>{item.fuenteOrigen || "N/D"}</td>
                  <td>{item.empresaOrigen || "N/D"}</td>
                  <td className="text-center">
                    <span className={`badge rounded-pill px-3 py-2 ${estadoBadgeClass(item.estadoInteres)}`}>
                      {estadoInteresLabel(item.estadoInteres)}
                    </span>
                    {item.transferido && !item.tieneMatricula && item.estadoInteres !== "descartado" && (
                      <div className="small text-primary mt-1">
                        <i className="bi bi-person-up me-1" />Transferido
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="d-flex justify-content-end gap-2">
                      {onMatricular && !item.tieneMatricula && item.estadoInteres !== "descartado" && (
                        <button
                          type="button"
                          className="btn btn-success btn-sm management-action-btn text-nowrap"
                          onClick={(e) => {
                            e.stopPropagation();
                            onMatricular(item);
                          }}
                          disabled={matriculandoId === item.id}
                          title="Matricular en una promoción"
                        >
                          {matriculandoId === item.id
                            ? <span className="spinner-border spinner-border-sm" role="status" />
                            : <><i className="bi bi-mortarboard" /><span className="d-none d-md-inline ms-1">Matricular</span></>}
                        </button>
                      )}
                      {canEdit && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm management-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit?.(item);
                          }}
                          title="Editar"
                        >
                          <i className="bi bi-pencil-square" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm management-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete?.(item);
                          }}
                          title="Eliminar"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
